/* Measured by TurnBar for its phone layout; its words never widen it. */
.container {
  container-type: inline-size;
}

/* Desktop: the turn on the left in two lines, the clock on the right. */
.bar {
  display: grid;
  grid-template-areas:
    'tag label clock'
    'answer answer clock';
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--zumpo-space-8);
  padding: var(--zumpo-space-16) var(--zumpo-space-16) var(--zumpo-space-16)
    var(--zumpo-space-24);
  border: 1px solid var(--zumpo-line);
  border-radius: var(--zumpo-radius-24);
  background: var(--zumpo-paper);
  color: var(--zumpo-ink);
}

/* Focused only where a game starts with nothing for the viewer to do. */
.bar:focus-visible {
  outline: var(--focus-ring) solid var(--zumpo-ink);
  outline-offset: var(--focus-ring);
}

.tag {
  grid-area: tag;
}

/* Without a category, what to do starts the first line. */
.untagged {
  grid-template-areas:
    'label label clock'
    'answer answer clock';
}

.label {
  grid-area: label;
  min-width: 0;
  overflow: hidden;
  color: var(--zumpo-muted);
  font: var(--zumpo-text-label);
  letter-spacing: var(--zumpo-text-label-tracking);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.clock {
  grid-area: clock;
  margin-left: var(--zumpo-space-16);
}

/* As tall as its main line's box, whatever the main line's scale: the main
   line's Fit watches this line for the room it has (Fit's hugs), which must
   not follow the scale it sets. */
.answer {
  display: flex;
  grid-area: answer;
  align-items: baseline;
  gap: var(--zumpo-space-12);
  min-width: 0;
  height: var(--zumpo-text-title-line);
}

.answer:has(.status) {
  height: var(--zumpo-text-heading-line);
}

/* One line whatever it says, so the bar keeps its height: the main line
   hugs its words and is scaled down (Fit) rather than wrap. The meta beside
   it keeps its words, and only the main line gives way: were both to shrink,
   the scaled main line would shrink its own box again and run over the
   meta. Only a meta wider than the whole line ends in an ellipsis. */
.answer > .mainBox {
  display: flex;
  flex: 0 1 auto;
  align-items: center;
  width: auto;
  min-width: 0;
  /* Scaled down, a long main line is shorter, not the bar. */
  height: var(--zumpo-text-title-line);
}

.answer > .mainBox:has(.status) {
  height: var(--zumpo-text-heading-line);
}

.main {
  white-space: nowrap;
}

.word {
  font: var(--zumpo-text-title);
  letter-spacing: var(--zumpo-text-title-tracking);
}

/* The server's hint string as is: blanks tracked, spaces of two-word answers kept. */
.hint {
  font: var(--zumpo-text-hint);
  letter-spacing: var(--zumpo-text-hint-tracking);
  white-space: pre;
}

.status {
  font: var(--zumpo-text-heading);
  letter-spacing: var(--zumpo-text-heading-tracking);
}

.meta {
  flex: none;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  color: var(--zumpo-muted);
  font: var(--zumpo-text-small);
  letter-spacing: var(--zumpo-text-small-tracking);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Desktop, narrower than a game's hints need beside its main line
   (TurnBar's hintRowBelow): the meta, often the only instruction on screen,
   has a line of its own under the main line, where it wraps rather than
   being cut. The line is kept while there is no meta, so the bar, and the
   board under it, keep their place from phase to phase. */
.rowBelow .bar {
  grid-template-areas:
    'tag label clock'
    'main main clock'
    'meta meta meta';
}

.rowBelow .untagged {
  grid-template-areas:
    'label label clock'
    'main main clock'
    'meta meta meta';
}

.rowBelow .answer {
  display: contents;
}

.rowBelow .answer > .mainBox {
  grid-area: main;
}

/* A word's height in every phase (TurnBar's wordLine). */
.desktop .wordLine .answer,
.desktop .wordLine .answer > .mainBox {
  height: var(--zumpo-text-title-line);
}

.rowBelow .meta {
  grid-area: meta;
  min-height: var(--zumpo-text-small-line);
  overflow: visible;
  white-space: normal;
}

/* Phone: a compact bar. The tag, what to do and the meta share the first
   line; the main line is under them at Heading size; the clock shows its
   time beside both. The bar takes it when it is 560 px wide or less, and on
   a screen too short for two lines, such as a phone on its side (TurnBar
   sets the class); at a phone's width the meta moves to a line of its own,
   below. Where what to do and the meta are too long for the first line
   together, they share it evenly and wrap, rather than one being cut. */
.phone .bar {
  grid-template-areas:
    'tag label meta . clock'
    'main main main main clock';
  grid-template-columns:
    minmax(0, max-content) minmax(0, max-content) minmax(0, max-content)
    minmax(0, 1fr) auto;
  gap: 0 var(--zumpo-space-8);
  padding: var(--zumpo-space-12) var(--zumpo-space-16);
  border-radius: var(--zumpo-radius-20);
}

.phone .untagged,
.phone .topless {
  grid-template-areas:
    'label label meta . clock'
    'main main main main clock';
}

/* A bar kept to its compact row on a phone (stackOnPhone false) keeps what
   to do and the meta on one line each, ending in an ellipsis, so the bar,
   and the board under it, keep their height from turn to turn; its words
   are written to fit. Kept below .narrow's own rules, so a stacked bar's
   label keeps its one scaled line. */
:where(.phone:not(.oneRow)) .label,
:where(.phone:not(.oneRow)) .meta {
  white-space: normal;
}

/* Without the label (labelOnPhone), the meta has the first line to itself;
   the label is said to a screen reader only, as the clock's purpose is. */
.phone .unlabelled {
  grid-template-areas:
    'tag meta meta meta clock'
    'main main main main clock';
}

.phone .untagged.unlabelled {
  grid-template-areas:
    'meta meta meta meta clock'
    'main main main main clock';
}

.phone .unlabelled .label {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* At a phone's width the first line has no room for the meta, which is
   often the only instruction on screen: it has a line of its own under the
   main line, the bar's whole width, with the tag at its start, and wraps
   rather than being cut. What to do has the first line to itself, beside
   the clock. The meta's line is kept while there is no meta, so the bar,
   and the board under it, keep their place from phase to phase. */
.narrow .bar {
  grid-template-areas:
    'label label clock'
    'main main clock'
    'tag meta meta';
  grid-template-columns: minmax(0, max-content) minmax(0, 1fr) auto;
}

.narrow .untagged {
  grid-template-areas:
    'label label clock'
    'main main clock'
    'meta meta meta';
}

/* Scaled down to its line, or wrapped where that would be too small to
   read (TurnBar's useOneLine), never cut. */
.narrow .label {
  overflow: visible;
  transform-origin: left center;
  white-space: nowrap;
}

.narrow .label[data-wrap] {
  white-space: normal;
}

.narrow .tag {
  margin-top: var(--zumpo-space-2);
}

.narrow .meta {
  min-height: var(--zumpo-text-small-line);
  margin-top: var(--zumpo-space-2);
  overflow: visible;
  white-space: normal;
}

/* Without the label (labelOnPhone), the meta takes its place on the first
   line, beside the clock, and wraps there; the label is said to a screen
   reader only, as the clock's purpose is. */
.narrow .unlabelled {
  grid-template-areas:
    'tag meta clock'
    'main main clock';
}

.narrow .untagged.unlabelled {
  grid-template-areas:
    'meta meta clock'
    'main main clock';
}

.narrow .unlabelled .meta {
  margin-top: 0;
}

.phone .answer {
  display: contents;
}

/* Scaled down, a long main line is shorter, not the bar: the line keeps its
   height and the words sit in the middle of it. */
.phone .answer > .mainBox {
  display: flex;
  grid-area: main;
  align-items: center;
  width: 100%;
  height: var(--zumpo-text-heading-line);
}

.phone .meta {
  grid-area: meta;
}

/* Beside what to do on the first line; a stacked bar's tag, on the meta's
   line, keeps only the 2 px over it, as Figma's Phone variant draws. */
.phone:not(.narrow) .tag {
  margin-bottom: var(--zumpo-space-2);
}

.phone .clock {
  margin: 0 0 0 var(--zumpo-space-4);
}

/* The clock says what it is for to a screen reader only. */
.phone .clock [data-countdown-label] {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.phone .word,
.phone .status {
  font: var(--zumpo-text-heading);
  letter-spacing: var(--zumpo-text-heading-tracking);
}

.phone .hint {
  font: var(--zumpo-text-hint-compact);
  letter-spacing: var(--zumpo-text-hint-compact-tracking);
}
